import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, Loader2, Archive } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

const TrashRow = ({ it, checked, onToggle }) => (
  <label data-testid={`trash-row-${it.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`trash-check-${it.id}`} className="h-4 w-4 accent-blue-600" />
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-slate-800 truncate">{it.name || "—"}</p>
      <p className="text-[11px] text-slate-500 truncate">{[it.national_id, it.branch, it.job_title].filter(Boolean).join(" · ")}</p>
      <p className="text-[11px] text-slate-400">حُذف {new Date(it.deleted_at).toLocaleDateString("ar-SA-u-nu-latn")}{it.deleted_by ? ` بواسطة ${it.deleted_by}` : ""}</p>
    </div>
    <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid={`trash-days-${it.id}`}>{daysLeft(it.purge_at)} يوم متبقٍ</span>
  </label>
);

export const EmployeesTrashDialog = ({ open, onClose, onRestored }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState("");
  const load = () => api.employeesTrash().then(setData).catch((e) => toast.error(e?.response?.data?.detail || "تعذّر تحميل السلة"));
  useEffect(() => { if (open) { setSel(new Set()); setData(null); load(); } }, [open]); // eslint-disable-line
  const items = data?.items || [];
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const ids = [...sel];

  const restore = async () => {
    setBusy("restore");
    try {
      const r = await api.restoreEmployeesTrash(ids);
      toast.success(`تم استرجاع ${r.restored} موظف`);
      if (r.skipped) toast.warning(`${r.skipped} موظف موجود مسبقاً ولم يُسترجع`);
      setSel(new Set()); load(); onRestored?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الاسترجاع"); } finally { setBusy(""); }
  };
  const purge = async () => {
    if (!window.confirm(`حذف ${ids.length} موظف نهائياً من السلة؟ لا يمكن التراجع.`)) return;
    setBusy("purge");
    try { const r = await api.purgeEmployeesTrash(ids); toast.success(`تم الحذف النهائي لـ ${r.purged} موظف`); setSel(new Set()); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); } finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl font-body" dir="rtl" data-testid="employees-trash-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5 text-amber-600" /> سلة الموظفين المحذوفين</DialogTitle>
          <DialogDescription>يبقى الموظف المحذوف هنا {data?.days || 30} يوماً ويمكن استرجاعه بكل بياناته، ثم يُحذف تلقائياً.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <button className="text-xs text-blue-600 underline" data-testid="trash-select-all" onClick={() => setSel(sel.size === items.length ? new Set() : new Set(items.map((i) => i.id)))}>
            {sel.size === items.length && items.length ? "إلغاء التحديد" : `تحديد الكل (${items.length})`}
          </button>
          <div className="ms-auto flex gap-2">
            <Button size="sm" className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={!sel.size || !!busy} onClick={restore} data-testid="trash-restore-btn">
              {busy === "restore" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} استرجاع ({sel.size})
            </Button>
            {data?.can_purge && (
              <Button size="sm" variant="outline" className="h-9 gap-1.5 border-rose-300 text-rose-600 hover:bg-rose-50" disabled={!sel.size || !!busy} onClick={purge} data-testid="trash-purge-btn">
                {busy === "purge" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} حذف نهائي ({sel.size})
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto space-y-2" data-testid="trash-list">
          {!data ? <div className="py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
            : items.length === 0 ? <p className="py-10 text-center text-sm text-slate-400" data-testid="trash-empty">السلة فارغة</p>
            : items.map((it) => <TrashRow key={it.id} it={it} checked={sel.has(it.id)} onToggle={() => toggle(it.id)} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const EmployeesTrashButton = ({ onRestored }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs text-amber-700 border-amber-200" data-testid="employees-trash-btn" onClick={() => setOpen(true)}>
        <Archive className="h-3.5 w-3.5" /> سلة المحذوفين
      </Button>
      <EmployeesTrashDialog open={open} onClose={() => setOpen(false)} onRestored={onRestored} />
    </>
  );
};
